(() => {
  if (document.documentElement.classList.contains('no-js')) return;

  const SUBSCRIPTION_DISCOUNT_MULTIPLIER = 0.8;

  function getActiveCurrencyCode() {
    if (window.Shopify?.currency?.active) return Shopify.currency.active;
    return 'USD';
  }

  function formatMoneyCents(cents) {
    if (typeof cents !== 'number' || Number.isNaN(cents)) return '';

    try {
      if (typeof window.Shopify?.formatMoney === 'function' && window.Shopify?.money_format) {
        return Shopify.formatMoney(cents, Shopify.money_format);
      }
    } catch (_) {}

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: getActiveCurrencyCode(),
        maximumFractionDigits: 2,
      }).format(cents / 100);
    } catch (_) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  function parseMoneyToCents(text) {
    if (!text) return null;

    const normalized = String(text).replace(/\s+/g, ' ').trim();
    const match = normalized.match(/(?:[$€£]\s*)?(\d{1,3}(?:[,\d]*)(?:\.\d{2})?)/);
    if (!match) return null;

    const numberString = match[1].replace(/,/g, '');
    const value = Number(numberString);
    if (!Number.isFinite(value)) return null;
    return Math.round(value * 100);
  }

  function parseAllMoneyToCents(text) {
    if (!text) return [];

    const normalized = String(text).replace(/\s+/g, ' ').trim();
    const results = [];
    const re = /[$€£]\s*(\d{1,3}(?:[,\d]*)(?:\.\d{2})?)/g;

    let match;
    while ((match = re.exec(normalized))) {
      const cents = parseMoneyToCents(match[0]);
      if (cents != null) results.push(cents);
    }

    return results;
  }

  function getCurrentVariantPriceCents() {
    const variantJsonScript = document.querySelector(
      '.product-info script[data-variant-info="json"]'
    );
    if (!variantJsonScript) return null;

    try {
      const variant = JSON.parse(variantJsonScript.textContent || '{}');
      const price = variant?.price;
      return typeof price === 'number' ? price : null;
    } catch (_) {
      return null;
    }
  }

  function isSubscriptionOption(optionEl) {
    return Boolean(
      optionEl.querySelector(
        [
          '.rc-option__subsave',
          '.rc-selling-plans__dropdown',
          '.rc-selling-plans',
          '[data-selling-plan]',
        ].join(',')
      )
    );
  }

  function getSubscriptionPriceCentsFromOption(optionEl) {
    const candidates = optionEl.querySelectorAll(
      [
        '.rc-option__price',
        '.rc-price',
        '.rc-price__value',
        '[data-rc-price]',
        '[data-price]',
        '.money',
      ].join(',')
    );

    for (const candidate of candidates) {
      const cents = parseMoneyToCents(candidate.textContent);
      if (cents != null) return cents;
    }

    const centsList = parseAllMoneyToCents(optionEl.textContent || '');
    if (centsList.length) return Math.min(...centsList);

    return null;
  }

  function ensurePricesEl(optionEl) {
    let pricesEl = optionEl.querySelector('.fsl-rc-option__prices');
    if (pricesEl) return pricesEl;

    pricesEl = document.createElement('div');
    pricesEl.className = 'fsl-rc-option__prices';
    optionEl.appendChild(pricesEl);
    return pricesEl;
  }

  function renderOptionPrices(optionEl, oneTimeCents) {
    if (oneTimeCents == null) return;

    const subscription = isSubscriptionOption(optionEl);
    const pricesEl = ensurePricesEl(optionEl);
    pricesEl.dataset.option = subscription ? 'subscription' : 'onetime';

    const priceEl = document.createElement('span');
    priceEl.className = 'fsl-rc-price';

    const compareEl = document.createElement('span');
    compareEl.className = 'fsl-rc-compare';

    const displayPriceCents = subscription
      ? getSubscriptionPriceCentsFromOption(optionEl) ?? Math.round(oneTimeCents * SUBSCRIPTION_DISCOUNT_MULTIPLIER)
      : oneTimeCents;

    priceEl.textContent = formatMoneyCents(displayPriceCents);
    pricesEl.replaceChildren(priceEl);

    if (subscription) {
      compareEl.textContent = formatMoneyCents(oneTimeCents);
      pricesEl.appendChild(compareEl);
    }
  }

  function enhanceRechargeWidget(widgetEl) {
    widgetEl.classList.add('fsl-rc-enhanced');

    const styleSubscriptionDetails = (root) => {
      const wrappers = root.querySelectorAll?.('.rc_popup_label_wrapper');
      if (!wrappers || wrappers.length === 0) return;

      wrappers.forEach((wrapper) => {
        wrapper.style.setProperty('display', 'flex', 'important');
        wrapper.style.setProperty('flex-direction', 'row', 'important');
        wrapper.style.setProperty('align-items', 'center', 'important');
        wrapper.style.setProperty('justify-content', 'flex-start', 'important');
        wrapper.style.setProperty('flex-wrap', 'nowrap', 'important');
        wrapper.style.setProperty('gap', '10px', 'important');
        wrapper.style.setProperty('margin', '0', 'important');
        wrapper.style.setProperty('padding', '0', 'important');
        wrapper.style.setProperty('font-family', 'var(--font-body-family)', 'important');
        wrapper.style.setProperty('font-size', '14px', 'important');
        wrapper.style.setProperty('font-weight', 'var(--font-body-weight)', 'important');
        wrapper.style.setProperty('line-height', '1.2', 'important');
        wrapper.style.setProperty('letter-spacing', 'normal', 'important');
        wrapper.style.setProperty('text-transform', 'none', 'important');

        const svg = wrapper.querySelector('svg');
        if (svg) {
          svg.style.setProperty('width', '18px', 'important');
          svg.style.setProperty('height', '18px', 'important');
          svg.style.setProperty('display', 'inline-block', 'important');
          svg.style.setProperty('margin', '0', 'important');
          svg.style.setProperty('flex-shrink', '0', 'important');
        }

        const label = wrapper.querySelector('.rc_popup__label');
        if (label) {
          label.style.setProperty('font-family', 'var(--font-body-family)', 'important');
          label.style.setProperty('font-size', '14px', 'important');
          label.style.setProperty('font-weight', 'var(--font-body-weight)', 'important');
          label.style.setProperty('line-height', '1.2', 'important');
          label.style.setProperty('letter-spacing', 'normal', 'important');
          label.style.setProperty('margin', '0', 'important');
          label.style.setProperty('display', 'inline-block', 'important');
        }
      });
    };

    const update = () => {
      const oneTimeCents = getCurrentVariantPriceCents();
      const optionEls = widgetEl.querySelectorAll('.rc-option');
      optionEls.forEach((optionEl) => renderOptionPrices(optionEl, oneTimeCents));
      styleSubscriptionDetails(document);
    };

    let raf = 0;
    const scheduleUpdate = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        update();
      });
    };

    scheduleUpdate();

    const widgetObserver = new MutationObserver(scheduleUpdate);
    widgetObserver.observe(widgetEl, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'value', 'checked', 'selected'],
    });

    const variantJsonScript = document.querySelector(
      '.product-info script[data-variant-info="json"]'
    );

    if (variantJsonScript) {
      const variantObserver = new MutationObserver(scheduleUpdate);
      variantObserver.observe(variantJsonScript, { childList: true, subtree: true, characterData: true });
    }

    document.addEventListener('variant:change', scheduleUpdate);
    document.addEventListener('variantChange', scheduleUpdate);
    document.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target.closest('.product-info')) scheduleUpdate();
    });

    widgetEl.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      if (
        target.closest(
          [
            '.rc-selling-plans__dropdown',
            'select',
            'option',
            'a',
            'button',
            'input',
          ].join(',')
        )
      ) {
        return;
      }

      const optionEl = target.closest('.rc-option');
      if (!optionEl) return;

      const radio = optionEl.querySelector('input[type="radio"]');
      if (!radio) return;
      if (radio.checked) return;

      radio.click();
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function initWhenReady() {
    const widgetEl = document.querySelector('.rc-widget');
    if (!widgetEl) return false;
    if (widgetEl.classList.contains('fsl-rc-enhanced')) return true;
    // enhanceRechargeWidget(widgetEl);
    return true;
  }

  if (initWhenReady()) return;

  const rootObserver = new MutationObserver(() => {
    if (initWhenReady()) rootObserver.disconnect();
  });

  rootObserver.observe(document.documentElement, { childList: true, subtree: true });
})();
